import { useMemo } from 'react';
import type { AnomalyCheckRow } from '../types/operations.types';
import { formatNumber, formatPercent } from '../helpers/etl-health.helpers';
import { EtlDetailTable } from './etl-detail-table';
import { EtlHealthStatusPill } from './etl-health-status-pill';

type Props = {
  rows: AnomalyCheckRow[];
  selectedDate: string | null;
};

export function AnomalyChecksPanel({ rows, selectedDate }: Props) {
  const filteredRows = useMemo(() => {
    if (!selectedDate) {
      return rows;
    }
    return rows.filter((row) => row.check_date === selectedDate);
  }, [rows, selectedDate]);

  const checkTypeOptions = useMemo(
    () => [...new Set(rows.map((row) => row.check_type))].sort(),
    [rows],
  );
  const statusOptions = useMemo(
    () => [...new Set(rows.map((row) => row.status))].sort(),
    [rows],
  );

  return (
    <EtlDetailTable
      rows={filteredRows}
      columns={[
        { id: 'date', header: 'Date', accessor: (row) => row.check_date, sortable: true },
        { id: 'platform', header: 'Platform', accessor: (row) => row.platform, sortable: true },
        { id: 'account_id', header: 'Account ID', accessor: (row) => row.account_id ?? '—', sortable: true },
        { id: 'check_type', header: 'Check type', accessor: (row) => row.check_type, sortable: true },
        {
          id: 'status',
          header: 'Status',
          accessor: (row) => row.status,
          sortable: true,
          render: (row) => <EtlHealthStatusPill status={row.status} />,
        },
        {
          id: 'observed_value',
          header: 'Observed',
          accessor: (row) => row.observed_value,
          sortable: true,
          align: 'right',
          render: (row) => formatNumber(row.observed_value),
        },
        {
          id: 'baseline_value',
          header: 'Baseline',
          accessor: (row) => row.baseline_value,
          sortable: true,
          align: 'right',
          render: (row) => formatNumber(row.baseline_value),
        },
        {
          id: 'deviation_pct',
          header: 'Deviation %',
          accessor: (row) => row.deviation_pct,
          sortable: true,
          align: 'right',
          render: (row) => formatPercent(row.deviation_pct),
        },
        { id: 'message', header: 'Message', accessor: (row) => row.message ?? '—' },
      ]}
      filters={[
        {
          id: 'check_type',
          label: 'Check type',
          options: checkTypeOptions,
          getValue: (row) => row.check_type,
        },
        {
          id: 'status',
          label: 'Status',
          options: statusOptions,
          getValue: (row) => row.status,
        },
      ]}
      emptyMessage="No anomaly checks for the selected period."
    />
  );
}
